// ============ SERVICIO DE INDEXEDDB PARA ALMACENAMIENTO LOCAL ============
// client/src/lib/db/indexed-db.ts

// Nombres de las bases de datos y stores
const DB_NAME = "qva-pos-offline";
const DB_VERSION = 4;

export const STORES = {
  INVENTORY: "inventory",
  PRODUCTS: "products",
  SALES: "sales",
  PENDING_OPERATIONS: "pendingOperations",
} as const;

// Tipos para los datos
export interface OfflineInventory {
  id: string;
  shopId: string;
  productId: string;
  price: string;
  stock: number;
  lowStockThreshold: number;
  acquisitionCost?: string;
  profitMargin?: string;
  supplierId?: string | null;
  supplierNotes?: string;
  batchNumber?: string;
  purchaseDate?: string;
  expiryDate?: string;
  minimumOrderQuantity?: number;
  leadTimeDays?: number;
  dailySalesRate?: string;
  reorderPoint?: number;
  suggestedPrice?: string;
  lastUpdated: string;
}

export interface OfflineProduct {
  id: string;
  name: string;
  category: string;
  lastUpdated: string;
}

// ============ INTERFAZ PARA VENTAS OFFLINE ============
export interface OfflineSale {
  id: string;
  shopId: string;
  shiftId?: string | null;
  customerId?: string | null;
  total: number;
  paymentMethod: string;
  changeGiven?: number;
  cashReceived?: number;

  // Datos del cliente (para ventas sin cliente registrado)
  fullName?: string | null;
  ci?: string | null;
  phone?: string | null;

  // Datos de transferencia
  transferCode?: string | null;
  bank?: string | null;
  firstFourDigits?: string | null;
  lastFourDigits?: string | null;
  // {NUEVOS CAMPOS PARA PAGO MIXTO - NO MODIFICAR LÓGICA EXISTENTE}
  destinationCard?: string | null;
  cashAmount?: number;
  transferAmount?: number;
  // {FIN NUEVOS CAMPOS}

  // Items de la venta
  items: OfflineSaleItem[];

  // Metadatos
  createdAt: string;
  synced: number;
  lastUpdated?: string;
}

// ============ INTERFAZ PARA ITEMS DE VENTA OFFLINE ============
export interface OfflineSaleItem {
  id?: string; // Opcional, se genera al sincronizar
  productId: string;
  quantity: number;
  price: number; // Precio unitario al momento de la venta
  inventoryId?: string; // Referencia al item de inventario (útil para offline)
}

// ============ INTERFAZ PARA OPERACIONES PENDIENTES ============
export interface PendingOperation {
  id: string;
  type: "create" | "update" | "delete";
  entityType: "inventory" | "product" | "sale" | "customer" | "shift";
  data: any;
  timestamp: string;
  retryCount: number;
  lastError?: string;

  // Campos para gestión de cola y conflictos
  shopId?: string;
  priority: "high" | "normal" | "low"; // high=ventas, normal=inventario, low=clientes
  maxRetries?: number; // Por defecto 3, configurable por operación
  originalCreatedAt: string; // Timestamp REAL de creación offline (para resolver conflictos en backend)

  // Estado de sincronización
  status: "pending" | "syncing" | "completed" | "failed" | "pending_approval";
  conflictData?: any; // Datos del conflicto si el backend rechazó por stock insuficiente
}

class IndexedDBService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  // Inicializar la base de datos
  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error("❌ Error abriendo IndexedDB:", request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log("✅ IndexedDB inicializada correctamente");
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Crear stores si no existen
        if (!db.objectStoreNames.contains(STORES.INVENTORY)) {
          const inventoryStore = db.createObjectStore(STORES.INVENTORY, {
            keyPath: "id",
          });
          inventoryStore.createIndex("shopId", "shopId", { unique: false });
          inventoryStore.createIndex("productId", "productId", {
            unique: false,
          });
          inventoryStore.createIndex("lastUpdated", "lastUpdated", {
            unique: false,
          });
          console.log("📦 Store creado:", STORES.INVENTORY);
        }

        if (!db.objectStoreNames.contains(STORES.PRODUCTS)) {
          const productsStore = db.createObjectStore(STORES.PRODUCTS, {
            keyPath: "id",
          });
          productsStore.createIndex("category", "category", { unique: false });
          productsStore.createIndex("lastUpdated", "lastUpdated", {
            unique: false,
          });
          console.log("📦 Store creado:", STORES.PRODUCTS);
        }

        if (!db.objectStoreNames.contains(STORES.SALES)) {
          const salesStore = db.createObjectStore(STORES.SALES, {
            keyPath: "id",
          });
          salesStore.createIndex("shopId", "shopId", { unique: false });
          salesStore.createIndex("createdAt", "createdAt", { unique: false });
          salesStore.createIndex("synced", "synced", { unique: false });
          // {NUEVO: Índice para buscar ventas por shiftId}
          salesStore.createIndex("shiftId", "shiftId", { unique: false });
          console.log("📦 Store creado:", STORES.SALES);
        }

        if (!db.objectStoreNames.contains(STORES.PENDING_OPERATIONS)) {
          const pendingStore = db.createObjectStore(STORES.PENDING_OPERATIONS, {
            keyPath: "id",
          });
          pendingStore.createIndex("type", "type", { unique: false });
          pendingStore.createIndex("entityType", "entityType", {
            unique: false,
          });
          pendingStore.createIndex("timestamp", "timestamp", { unique: false });
          pendingStore.createIndex("retryCount", "retryCount", {
            unique: false,
          });
          console.log("📦 Store creado:", STORES.PENDING_OPERATIONS);
        }

        // {NUEVO: Store para guardar el inventario completo para modo offline}
        if (!db.objectStoreNames.contains("offline_inventory")) {
          const offlineStore = db.createObjectStore("offline_inventory", {
            keyPath: "shopId",
          });
          offlineStore.createIndex("updatedAt", "updatedAt", { unique: false });
          console.log("📦 Store creado: offline_inventory");
        }

        // {NUEVO: Store para turnos offline}
        if (!db.objectStoreNames.contains("offline_shifts")) {
          const shiftStore = db.createObjectStore("offline_shifts", {
            keyPath: "id",
          });
          shiftStore.createIndex("shopId", "shopId", { unique: false });
          shiftStore.createIndex("status", "status", { unique: false });
          shiftStore.createIndex("synced", "synced", { unique: false });
          console.log("📦 Store creado: offline_shifts");
        }

        // {NUEVO: Store para sesión offline - JWT y validación local}
        if (!db.objectStoreNames.contains("session")) {
          const sessionStore = db.createObjectStore("session", {
            keyPath: "id",
          });
          sessionStore.createIndex("username", "username", { unique: true });
          sessionStore.createIndex("expiresAt", "expiresAt", { unique: false });
          console.log("📦 Store creado: session");
        }

        // {NUEVO: Store para metadatos de sincronización}
        if (!db.objectStoreNames.contains("sync_meta")) {
          db.createObjectStore("sync_meta", {
            keyPath: "key",
          });
          console.log("📦 Store creado: sync_meta");
        }

        // Store para caché de cajas registradoras (usado offline)
        if (!db.objectStoreNames.contains("cash_registers")) {
          db.createObjectStore("cash_registers", {
            keyPath: "shopId",
          });
          console.log("📦 Store creado: cash_registers");
        }

        // Store para sesión de owner offline
        if (!db.objectStoreNames.contains("owner_session")) {
          db.createObjectStore("owner_session", {
            keyPath: "id",
          });
          console.log("📦 Store creado: owner_session");
        }

        // Migración v3→v4: synced boolean → number
        const oldVersion = event.oldVersion;
        if (oldVersion < 4) {
          for (const storeName of ["sales", "offline_shifts"]) {
            if (db.objectStoreNames.contains(storeName)) {
              const tx = (event.target as IDBOpenDBRequest).transaction!;
              const store = tx.objectStore(storeName);
              const request = store.openCursor();
              request.onsuccess = () => {
                const cursor = request.result;
                if (cursor) {
                  const record = cursor.value;
                  if (typeof record.synced === "boolean") {
                    record.synced = record.synced ? 1 : 0;
                    cursor.update(record);
                    console.log(`🔄 Migrado synced en ${storeName}: ${record.id}`);
                  }
                  cursor.continue();
                }
              };
            }
          }
        }
      };
    });

    return this.initPromise;
  }

  // Guardar inventario completo
  async saveInventory(inventory: OfflineInventory[]): Promise<void> {
    await this.init();
    const tx = this.db!.transaction(STORES.INVENTORY, "readwrite");
    const store = tx.objectStore(STORES.INVENTORY);

    for (const item of inventory) {
      store.put(item);
    }

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => {
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  // Obtener inventario por tienda
  async getInventory(shopId: string): Promise<OfflineInventory[]> {
    await this.init();
    const tx = this.db!.transaction(STORES.INVENTORY, "readonly");
    const store = tx.objectStore(STORES.INVENTORY);
    const index = store.index("shopId");

    return new Promise((resolve, reject) => {
      const request = index.getAll(shopId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Guardar un item de inventario
  async saveInventoryItem(item: OfflineInventory): Promise<void> {
    await this.init();
    const tx = this.db!.transaction(STORES.INVENTORY, "readwrite");
    const store = tx.objectStore(STORES.INVENTORY);

    return new Promise((resolve, reject) => {
      const request = store.put(item);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Guardar productos
  async saveProducts(products: OfflineProduct[]): Promise<void> {
    await this.init();
    const tx = this.db!.transaction(STORES.PRODUCTS, "readwrite");
    const store = tx.objectStore(STORES.PRODUCTS);

    for (const product of products) {
      store.put(product);
    }

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => {
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  // Obtener todos los productos
  async getProducts(): Promise<OfflineProduct[]> {
    await this.init();
    const tx = this.db!.transaction(STORES.PRODUCTS, "readonly");
    const store = tx.objectStore(STORES.PRODUCTS);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Guardar venta
  async saveSale(sale: OfflineSale): Promise<void> {
    await this.init();

    const saleToSave = {
      ...sale,
      synced: sale.synced ? 1 : 0,
    };

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORES.SALES, "readwrite");
      const store = tx.objectStore(STORES.SALES);
      const request = store.put(saleToSave);

      // CORRECCIÓN: Escuchar directamente el complete de la transacción, no anidar en request.onsuccess
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      request.onerror = () => reject(request.error); // Fallback si request falla antes que tx
    });
  }
  // COMENTARIO:
  // - Anidar tx.oncomplete dentro de request.onsuccess puede causar que la transacción se complete antes de tiempo.
  // - Este patrón es más robusto y sigue las mejores prácticas de IndexedDB.

  // Obtener ventas no sincronizadas
  async getUnsyncedSales(shopId: string): Promise<OfflineSale[]> {
    try {
      await this.init();

      const tx = this.db!.transaction(STORES.SALES, "readonly");
      const store = tx.objectStore(STORES.SALES);
      const index = store.index("synced");
      const keyRange = IDBKeyRange.only(0); // synced es number (0=pendiente, 1=ok)
      // COMENTARIO: Esto asegura que el índice "synced" filtre correctamente las ventas pendientes

      return new Promise((resolve) => {
        const request = index.getAll(keyRange);

        request.onsuccess = () => {
          const allUnsynced = request.result || [];
          const filtered = allUnsynced.filter((sale) => sale.shopId === shopId);
          resolve(filtered);
        };

        request.onerror = () => resolve([]);
      });
    } catch (error) {
      console.error("Error obteniendo ventas no sincronizadas:", error);
      return [];
    }
  }

  // Agregar operación pendiente
  async addPendingOperation(operation: PendingOperation): Promise<void> {
    await this.init();
    const tx = this.db!.transaction(STORES.PENDING_OPERATIONS, "readwrite");
    const store = tx.objectStore(STORES.PENDING_OPERATIONS);

    return new Promise((resolve, reject) => {
      const request = store.put(operation);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Obtener operaciones pendientes
  async getPendingOperations(): Promise<PendingOperation[]> {
    await this.init();
    const tx = this.db!.transaction(STORES.PENDING_OPERATIONS, "readonly");
    const store = tx.objectStore(STORES.PENDING_OPERATIONS);
    const index = store.index("timestamp");

    return new Promise((resolve, reject) => {
      const request = index.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Eliminar operación pendiente
  async removePendingOperation(id: string): Promise<void> {
    await this.init();
    const tx = this.db!.transaction(STORES.PENDING_OPERATIONS, "readwrite");
    const store = tx.objectStore(STORES.PENDING_OPERATIONS);

    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => {
        console.log(`✅ Operación pendiente eliminada: ${id}`);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Limpiar todos los datos de una tienda
  async clearShopData(shopId: string): Promise<void> {
    await this.init();

    // Limpiar inventario
    const tx1 = this.db!.transaction(STORES.INVENTORY, "readwrite");
    const inventoryStore = tx1.objectStore(STORES.INVENTORY);
    const inventoryIndex = inventoryStore.index("shopId");
    const inventoryItems = await new Promise<OfflineInventory[]>(
      (resolve, reject) => {
        const request = inventoryIndex.getAll(shopId);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      },
    );

    for (const item of inventoryItems) {
      inventoryStore.delete(item.id);
    }

    // Limpiar ventas
    const tx2 = this.db!.transaction(STORES.SALES, "readwrite");
    const salesStore = tx2.objectStore(STORES.SALES);
    const salesIndex = salesStore.index("shopId");
    const sales = await new Promise<OfflineSale[]>((resolve, reject) => {
      const request = salesIndex.getAll(shopId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    for (const sale of sales) {
      salesStore.delete(sale.id);
    }

    console.log(`🧹 Datos de tienda ${shopId} limpiados`);
  }

  // ============ ACTUALIZAR OPERACIÓN PENDIENTE ============
  async updatePendingOperation(
    id: string,
    updates: Partial<PendingOperation>,
  ): Promise<void> {
    await this.init();
    const tx = this.db!.transaction(STORES.PENDING_OPERATIONS, "readwrite");
    const store = tx.objectStore(STORES.PENDING_OPERATIONS);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const operation = getRequest.result;
        if (operation) {
          const updatedOperation = { ...operation, ...updates };
          const putRequest = store.put(updatedOperation);

          putRequest.onsuccess = () => {
            console.log(`✅ Operación pendiente actualizada: ${id}`);
            resolve();
          };

          putRequest.onerror = () => reject(putRequest.error);
        } else {
          reject(new Error(`Operación ${id} no encontrada`));
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // ============ ELIMINAR PRODUCTO ============
  async deleteProduct(id: string): Promise<void> {
    await this.init();
    const tx = this.db!.transaction(STORES.PRODUCTS, "readwrite");
    const store = tx.objectStore(STORES.PRODUCTS);

    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => {
        console.log(`✅ Producto eliminado de IndexedDB: ${id}`);
        resolve();
      };
      request.onerror = () => {
        console.error(`❌ Error eliminando producto ${id}:`, request.error);
        reject(request.error);
      };
    });
  }

  // ============ ELIMINAR ITEM DE INVENTARIO ============
  async deleteInventoryItem(id: string): Promise<void> {
    await this.init();
    const tx = this.db!.transaction(STORES.INVENTORY, "readwrite");
    const store = tx.objectStore(STORES.INVENTORY);

    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => {
        console.log(`✅ Item de inventario eliminado de IndexedDB: ${id}`);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ============ ACTUALIZAR ITEM DE INVENTARIO ============
  // {CORRECCIÓN: Versión corregida que actualiza correctamente ambos stores}
  async updateInventoryItem(
    id: string,
    updates: Partial<OfflineInventory>,
    shopId?: string,
  ): Promise<void> {
    await this.init();

    console.log(`📦 [OFFLINE] Actualizando item de inventario: ${id}`, updates);

    // 1. Actualizar en store INVENTORY (donde lee la UI)
    const tx1 = this.db!.transaction(STORES.INVENTORY, "readwrite");
    const inventoryStore = tx1.objectStore(STORES.INVENTORY);

    await new Promise<void>((resolve, reject) => {
      const getRequest = inventoryStore.get(id);

      getRequest.onsuccess = () => {
        const item = getRequest.result;
        if (item) {
          const updatedItem = {
            ...item,
            ...updates,
            lastUpdated: new Date().toISOString(),
          };

          const putRequest = inventoryStore.put(updatedItem);
          putRequest.onsuccess = () => {
            console.log(`✅ [OFFLINE] Item actualizado en INVENTORY: ${id}`);
            resolve();
          };
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          console.warn(`⚠️ [OFFLINE] Item no encontrado en INVENTORY: ${id}`);
          resolve(); // No fallamos, solo continuamos
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });

    // 2. Actualizar en store OFFLINE_INVENTORY (respaldo completo)
    // {CORRECCIÓN: Buscar por shopId y luego por item.id dentro del array}
    const tx2 = this.db!.transaction("offline_inventory", "readwrite");
    const offlineStore = tx2.objectStore("offline_inventory");

    // Obtener el item actual para saber su shopId
    const currentItem = await this.getInventoryItem(id);
    const shopIdToUse = currentItem?.shopId || shopId;
    if (shopIdToUse) {
      const shopInventory = await new Promise<any>((resolve, reject) => {
        const request = offlineStore.get(shopIdToUse);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (shopInventory && shopInventory.inventory) {
        const itemIndex = shopInventory.inventory.findIndex(
          (item: any) => item.id === id,
        );

        if (itemIndex !== -1) {
          shopInventory.inventory[itemIndex] = {
            ...shopInventory.inventory[itemIndex],
            ...updates,
            lastUpdated: new Date().toISOString(),
          };
          shopInventory.updatedAt = new Date().toISOString();

          await new Promise<void>((resolve, reject) => {
            const putRequest = offlineStore.put(shopInventory);
            putRequest.onsuccess = () => {
              console.log(
                `✅ [OFFLINE] Item actualizado en OFFLINE_INVENTORY: ${id}`,
              );
              resolve();
            };
            putRequest.onerror = () => reject(putRequest.error);
          });
        } else {
          console.warn(
            `⚠️ [OFFLINE] Item no encontrado en OFFLINE_INVENTORY: ${id}`,
          );
        }
      }
    }
  }

  // ============ OBTENER VENTAS POR TIENDA ============
  async getSales(
    shopId: string,
    includeSynced: boolean = true,
  ): Promise<OfflineSale[]> {
    await this.init();
    const tx = this.db!.transaction(STORES.SALES, "readonly");
    const store = tx.objectStore(STORES.SALES);
    const index = store.index("shopId");

    return new Promise((resolve, reject) => {
      const request = index.getAll(shopId);
      request.onsuccess = () => {
        let sales = request.result;
        if (!includeSynced) {
          sales = sales.filter((sale) => sale.synced === 0);
        }
        // Ordenar por fecha descendente (más recientes primero)
        sales.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        resolve(sales);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ============ ACTUALIZAR ESTADO DE SINCRONIZACIÓN DE VENTA ============
  async markSaleAsSynced(id: string): Promise<void> {
    await this.init();
    const tx = this.db!.transaction(STORES.SALES, "readwrite");
    const store = tx.objectStore(STORES.SALES);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const sale = getRequest.result;
        if (sale) {
          sale.synced = 1;
          sale.lastUpdated = new Date().toISOString();

          const putRequest = store.put(sale);
          putRequest.onsuccess = () => {
            console.log(`✅ Venta marcada como sincronizada: ${id}`);
            resolve();
          };
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(); // Si no existe, consideramos que ya no es relevante
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // ============ OBTENER OPERACIONES PENDIENTES POR TIENDA ============
  async getPendingOperationsByShop(
    shopId: string,
  ): Promise<PendingOperation[]> {
    await this.init();
    const operations = await this.getPendingOperations();
    return operations.filter((op) => op.shopId === shopId);
  }

  // ============ ELIMINAR OPERACIONES PENDIENTES POR TIPO ============
  async removePendingOperationsByType(
    entityType: string,
    entityId: string,
  ): Promise<void> {
    await this.init();
    const operations = await this.getPendingOperations();
    const operationsToRemove = operations.filter(
      (op) => op.entityType === entityType && op.data?.id === entityId,
    );

    for (const op of operationsToRemove) {
      await this.removePendingOperation(op.id);
    }
  }

  // {NUEVO: Guardar inventario completo para modo offline}
  async saveFullInventoryForOffline(
    shopId: string,
    inventory: any[],
  ): Promise<void> {
    await this.init();
    const tx = this.db!.transaction("offline_inventory", "readwrite");
    const store = tx.objectStore("offline_inventory");

    // Guardar el inventario con metadata
    const data = {
      shopId,
      inventory,
      updatedAt: new Date().toISOString(),
    };

    return new Promise((resolve, reject) => {
      const request = store.put(data);
      request.onsuccess = () => {
        console.log(
          `📦 Inventario offline guardado para tienda ${shopId}: ${inventory.length} productos`,
        );
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // {NUEVO: Obtener inventario guardado para modo offline}
  async getOfflineInventory(shopId: string): Promise<any[] | null> {
    await this.init();
    const tx = this.db!.transaction("offline_inventory", "readonly");
    const store = tx.objectStore("offline_inventory");

    return new Promise((resolve, reject) => {
      const request = store.get(shopId);
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          console.log(
            `📦 Inventario offline cargado: ${result.inventory.length} productos`,
          );
          resolve(result.inventory);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // {NUEVO: Guardar turno offline}
  async saveOfflineShift(shift: any): Promise<void> {
    await this.init();
    const tx = this.db!.transaction("offline_shifts", "readwrite");
    const store = tx.objectStore("offline_shifts");

    const shiftToSave = {
      ...shift,
      synced: shift.synced ? 1 : 0,
      createdAt: new Date().toISOString(),
    };

    return new Promise((resolve, reject) => {
      const request = store.put(shiftToSave);
      request.onsuccess = () => {
        console.log(`📦 Turno offline guardado: ${shift.id}`);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // {NUEVO: Obtener turno activo offline}
  async getActiveOfflineShift(shopId: string): Promise<any | null> {
    await this.init();
    const tx = this.db!.transaction("offline_shifts", "readonly");
    const store = tx.objectStore("offline_shifts");
    const index = store.index("shopId");

    return new Promise((resolve, reject) => {
      const request = index.getAll(shopId);
      request.onsuccess = () => {
        const shifts = request.result || [];

        // {CORRECCIÓN: Buscar turnos abiertos, sin importar synced}
        // Prioridad: 1. Turno offline no sincronizado, 2. Backup de turno online
        const offlineShift = shifts.find(
          (s: any) => s.status === "open" && s.synced === 0,
        );
        const backupShift = shifts.find(
          (s: any) => s.status === "open" && s.isBackup === true,
        );

        const activeShift = offlineShift || backupShift;

        console.log(
          `📦 [OFFLINE] Turnos encontrados: ${shifts.length}, Activo: ${!!activeShift} (offline: ${!!offlineShift}, backup: ${!!backupShift})`,
        );
        resolve(activeShift || null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // {NUEVO: Marcar turno como sincronizado}
  async markShiftAsSynced(shiftId: string): Promise<void> {
    await this.init();
    const tx = this.db!.transaction("offline_shifts", "readwrite");
    const store = tx.objectStore("offline_shifts");

    const getRequest = store.get(shiftId);

    return new Promise((resolve, reject) => {
      getRequest.onsuccess = () => {
        const shift = getRequest.result;
        if (shift) {
          shift.synced = 1;
          store.put(shift);
          console.log(`✅ Turno marcado como sincronizado: ${shiftId}`);
        }
        resolve();
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // {NUEVO: Obtener un item de inventario por ID}
  async getInventoryItem(id: string): Promise<OfflineInventory | null> {
    await this.init();
    const tx = this.db!.transaction("offline_inventory", "readonly");
    const store = tx.objectStore("offline_inventory");

    // Primero obtener el objeto completo (que tiene shopId como key)
    const allInventory = await new Promise<any[]>((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    // Buscar en todos los inventarios el item por ID
    for (const shopInventory of allInventory) {
      const item = shopInventory.inventory?.find((item: any) => item.id === id);
      if (item) {
        return item;
      }
    }

    return null;
  }

  // {NUEVO: Eliminar un turno offline por ID}
  async deleteOfflineShift(shiftId: string): Promise<void> {
    await this.init();
    const tx = this.db!.transaction("offline_shifts", "readwrite");
    const store = tx.objectStore("offline_shifts");

    return new Promise((resolve, reject) => {
      const request = store.delete(shiftId);
      request.onsuccess = () => {
        console.log(`🗑️ Turno offline eliminado: ${shiftId}`);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // {NUEVO: Marcar turno como cerrado localmente}
  async markShiftAsClosed(shiftId: string, countedCash: string): Promise<void> {
    await this.init();
    const tx = this.db!.transaction("offline_shifts", "readwrite");
    const store = tx.objectStore("offline_shifts");

    const getRequest = store.get(shiftId);

    return new Promise((resolve, reject) => {
      getRequest.onsuccess = () => {
        const shift = getRequest.result;
        if (shift) {
          shift.status = "closed";
          shift.countedCash = countedCash;
          shift.closedAt = new Date().toISOString();
          shift.closedLocal = true;

          const putRequest = store.put(shift);
          putRequest.onsuccess = () => {
            console.log(`✅ Turno marcado como cerrado localmente: ${shiftId}`);
            resolve();
          };
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          console.warn(`⚠️ Turno no encontrado: ${shiftId}`);
          resolve();
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // {NUEVO: Marcar turno como pendiente de autorización localmente}
  async markShiftAsPendingAuthorization(
    shiftId: string,
    countedCash: string,
    expectedCash: string,
    difference: string,
    denominationBreakdown?: Record<string, number>,
    notes?: string,
  ): Promise<void> {
    await this.init();
    const tx = this.db!.transaction("offline_shifts", "readwrite");
    const store = tx.objectStore("offline_shifts");

    const getRequest = store.get(shiftId);

    return new Promise((resolve, reject) => {
      getRequest.onsuccess = () => {
        const shift = getRequest.result;
        if (shift) {
          shift.status = "pending_authorization";
          shift.countedCash = countedCash;
          shift.expectedCash = expectedCash;
          shift.difference = difference;
          shift.closedAt = new Date().toISOString();
          shift.closedLocal = true;
          shift.requiresAuthorization = true;
          if (denominationBreakdown) shift.denominationBreakdown = denominationBreakdown;
          if (notes !== undefined) shift.notes = notes;

          const putRequest = store.put(shift);
          putRequest.onsuccess = () => {
            console.log(
              `⚠️ Turno marcado como pendiente de autorización: ${shiftId}`,
            );
            resolve();
          };
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          console.warn(`⚠️ Turno no encontrado: ${shiftId}`);
          resolve();
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // {NUEVO: Obtener turno offline por ID}
  async getOfflineShiftById(shiftId: string): Promise<any | null> {
    await this.init();
    const tx = this.db!.transaction("offline_shifts", "readonly");
    const store = tx.objectStore("offline_shifts");

    return new Promise((resolve, reject) => {
      const request = store.get(shiftId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  // {NUEVO: Obtener turno offline pendiente de autorización local}
  async getPendingAuthorizationOfflineShift(shopId: string): Promise<any | null> {
    await this.init();
    const tx = this.db!.transaction("offline_shifts", "readonly");
    const store = tx.objectStore("offline_shifts");
    const index = store.index("shopId");

    return new Promise((resolve, reject) => {
      const request = index.getAll(shopId);
      request.onsuccess = () => {
        const shifts = request.result || [];
        const pendingAuthShift = shifts.find(
          (s: any) =>
            s.status === "pending_authorization",
        );
        resolve(pendingAuthShift || null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // {NUEVO: Actualizar shiftId en ventas pendientes}
  // {NUEVO: Actualizar shiftId en ventas pendientes - VERSIÓN CORREGIDA}
  async updateSalesShiftId(
    oldShiftId: string,
    newShiftId: string,
  ): Promise<void> {
    await this.init();

    console.log(
      `🔄 [DB] Actualizando shiftId en ventas: ${oldShiftId} -> ${newShiftId}`,
    );

    // Usar el store STORES.SALES
    const tx = this.db!.transaction(STORES.SALES, "readwrite");
    const store = tx.objectStore(STORES.SALES);

    // Obtener todas las ventas
    const allSales = await new Promise<any[]>((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    // Filtrar ventas que tienen el oldShiftId
    const salesToUpdate = allSales.filter(
      (sale) => sale.shiftId === oldShiftId,
    );

    console.log(`📊 [DB] Ventas a actualizar: ${salesToUpdate.length}`);

    for (const sale of salesToUpdate) {
      sale.shiftId = newShiftId;
      await new Promise<void>((resolve, reject) => {
        const putRequest = store.put(sale);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      });
    }

    console.log(
      `✅ [DB] ${salesToUpdate.length} ventas actualizadas con nuevo shiftId: ${newShiftId}`,
    );
  }

  // ============ GESTIÓN DE SESIÓN OFFLINE ============

  // Guardar sesión con token JWT y hash de contraseña (NUNCA texto plano)
  async saveSession(session: {
    id: string;
    username: string;
    token: string;
    passwordHash: string; // SHA-256 hash generado en auth-offline.ts
    expiresAt: number; // Timestamp en milisegundos
    shopId: string;
    userId?: string; // ID real del usuario en BD (no sintético)
  }): Promise<void> {
    await this.init();
    const tx = this.db!.transaction("session", "readwrite");
    const store = tx.objectStore("session");

    return new Promise((resolve, reject) => {
      // Limpiar sesiones anteriores antes de guardar la nueva
      store.clear().onsuccess = () => {
        const request = store.put(session);
        request.onsuccess = () => {
          console.log(`✅ Sesión guardada para offline: ${session.username}`);
          resolve();
        };
        request.onerror = () => reject(request.error);
      };
    });
  }

  // Obtener sesión activa para validar login offline
  // CORRECCIÓN: Se devuelve el objeto completo (incluyendo id y passwordHash)
  // para permitir actualizaciones (como refreshSession) sin perder datos críticos.
  async getActiveSession(): Promise<{
    id: string;
    username: string;
    token: string;
    passwordHash: string;
    expiresAt: number;
    shopId: string;
    userId?: string;
  } | null> {
    await this.init();
    const tx = this.db!.transaction("session", "readonly");
    const store = tx.objectStore("session");

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const sessions = request.result || [];
        const active = sessions[0]; // Solo una sesión activa por dispositivo
        if (active && active.expiresAt > Date.now()) {
          resolve({
            id: active.id,
            username: active.username,
            token: active.token,
            passwordHash: active.passwordHash, // CORRECCIÓN: Exponer hash para permitir updates seguros
            expiresAt: active.expiresAt,
            shopId: active.shopId,
            userId: active.userId,
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }
  // COMENTARIO: Ahora el objeto retornado coincide con el tipo que exige saveSession.

  // Eliminar sesión (logout seguro)
  async clearSession(): Promise<void> {
    await this.init();
    const tx = this.db!.transaction("session", "readwrite");
    const store = tx.objectStore("session");

    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => {
        console.log("🔒 Sesión eliminada - logout completado");
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ============ CACHÉ DE CAJAS REGISTRADORAS ============

  async saveCashRegisters(shopId: string, registers: any[]): Promise<void> {
    await this.init();
    const tx = this.db!.transaction("cash_registers", "readwrite");
    const store = tx.objectStore("cash_registers");
    return new Promise((resolve, reject) => {
      const request = store.put({ shopId, registers, updatedAt: new Date().toISOString() });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getCachedCashRegisters(shopId: string): Promise<any[] | null> {
    await this.init();
    const tx = this.db!.transaction("cash_registers", "readonly");
    const store = tx.objectStore("cash_registers");
    return new Promise((resolve, reject) => {
      const request = store.get(shopId);
      request.onsuccess = () => {
        const data = request.result;
        resolve(data ? data.registers : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ============ SESIÓN DE OWNER OFFLINE ============

  async saveOwnerSession(session: {
    id: string;
    email: string;
    token: string;
    passwordHash: string;
    expiresAt: number;
    ownerId: string;
    planType?: string;
    currency?: string;
  }): Promise<void> {
    await this.init();
    const tx = this.db!.transaction("owner_session", "readwrite");
    const store = tx.objectStore("owner_session");
    return new Promise((resolve, reject) => {
      store.clear().onsuccess = () => {
        const request = store.put(session);
        request.onsuccess = () => {
          console.log(`✅ Sesión de owner guardada para offline: ${session.email}`);
          resolve();
        };
        request.onerror = () => reject(request.error);
      };
    });
  }

  async getActiveOwnerSession(): Promise<{
    id: string;
    email: string;
    token: string;
    passwordHash: string;
    expiresAt: number;
    ownerId: string;
    planType?: string;
    currency?: string;
  } | null> {
    await this.init();
    const tx = this.db!.transaction("owner_session", "readonly");
    const store = tx.objectStore("owner_session");
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const sessions = request.result || [];
        const active = sessions[0];
        if (active && active.expiresAt > Date.now()) {
          resolve(active);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async clearOwnerSession(): Promise<void> {
    await this.init();
    const tx = this.db!.transaction("owner_session", "readwrite");
    const store = tx.objectStore("owner_session");
    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => {
        console.log("🔒 Sesión de owner eliminada");
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ============ METADATOS DE SINCRONIZACIÓN ============

  // Guardar timestamp de última sincronización exitosa por tipo
  async setSyncTimestamp(key: string, timestamp: number): Promise<void> {
    await this.init();
    const tx = this.db!.transaction("sync_meta", "readwrite");
    const store = tx.objectStore("sync_meta");

    return new Promise((resolve, reject) => {
      const request = store.put({ key, value: timestamp });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Obtener timestamp de última sincronización
  async getSyncTimestamp(key: string): Promise<number | null> {
    await this.init();
    const tx = this.db!.transaction("sync_meta", "readonly");
    const store = tx.objectStore("sync_meta");

    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result?.value || null);
      request.onerror = () => reject(request.error);
    });
  }

  /// ============ COLA PRIORIZADA ============

  // Tipo auxiliar para priorización (evita errores de indexación en TypeScript)
  private readonly PRIORITY_ORDER: Record<"high" | "normal" | "low", number> = {
    high: 1,
    normal: 2,
    low: 3,
  };

  // Obtener operaciones pendientes ordenadas por prioridad y antigüedad
  async getPrioritizedPendingOperations(
    shopId?: string,
  ): Promise<PendingOperation[]> {
    await this.init();
    const tx = this.db!.transaction(STORES.PENDING_OPERATIONS, "readonly");
    const store = tx.objectStore(STORES.PENDING_OPERATIONS);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        let ops = (request.result as PendingOperation[]) || [];

        // Filtrar operaciones que ya fallaron definitivamente
        ops = ops.filter((op) => op.status !== "failed");

        // Filtrar por tienda si se especifica
        if (shopId) {
          ops = ops.filter((op) => op.shopId === shopId);
        }

        // Ordenar: 1) prioridad (high > normal > low), 2) originalCreatedAt (más antiguo primero)
        ops.sort((a, b) => {
          // CORRECCIÓN: Usar type assertion segura para indexar PRIORITY_ORDER
          const prioA =
            this.PRIORITY_ORDER[
              a.priority as keyof typeof this.PRIORITY_ORDER
            ] ?? 99;
          const prioB =
            this.PRIORITY_ORDER[
              b.priority as keyof typeof this.PRIORITY_ORDER
            ] ?? 99;

          const prioDiff = prioA - prioB;
          if (prioDiff !== 0) return prioDiff;

          // Fallback: ordenar por fecha de creación real (offline)
          const timeA = new Date(a.originalCreatedAt).getTime();
          const timeB = new Date(b.originalCreatedAt).getTime();
          return timeA - timeB;
        });

        resolve(ops);
      };
      request.onerror = () => reject(request.error);
    });
  }
  // COMENTARIO:
  // - PRIORITY_ORDER como propiedad privada evita recrear el objeto en cada llamada.
  // - "as keyof typeof this.PRIORITY_ORDER" le dice a TypeScript que a.priority es una clave válida.
  // - El operador ?? 99 es más seguro que || porque no confunde 0 con falso.
  // ============ FIN COLA PRIORIZADA ============

  // Actualizar estado de una operación en cola
  async updateOperationStatus(
    id: string,
    status: PendingOperation["status"],
    conflictData?: any,
  ): Promise<void> {
    await this.init();
    const tx = this.db!.transaction(STORES.PENDING_OPERATIONS, "readwrite");
    const store = tx.objectStore(STORES.PENDING_OPERATIONS);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(id);
      getRequest.onsuccess = () => {
        const op = getRequest.result;
        if (op) {
          op.status = status;
          if (conflictData) op.conflictData = conflictData;
          if (status === "completed" || status === "failed") {
            op.retryCount = op.maxRetries || 3; // Marcar como procesada
          }
          const putRequest = store.put(op);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(); // Operación ya eliminada, no es error
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // Contar operaciones pendientes por estado
  async getPendingCountByStatus(shopId?: string): Promise<{
    pending: number;
    failed: number;
    pending_approval: number;
  }> {
    await this.init();
    const ops = await this.getPrioritizedPendingOperations(shopId);
    return {
      pending: ops.filter((o) => o.status === "pending").length,
      failed: ops.filter((o) => o.status === "failed").length,
      pending_approval: ops.filter((o) => o.status === "pending_approval")
        .length,
    };
  }

  // Limpiar operaciones completadas (mantener solo pendientes y fallidas recientes)
  async cleanupCompletedOperations(keepFailedHours = 24): Promise<void> {
    await this.init();
    const ops = await this.getPendingOperations();
    const cutoff = Date.now() - keepFailedHours * 60 * 60 * 1000;

    const toDelete = ops.filter(
      (op) =>
        op.status === "completed" ||
        (op.status === "failed" && new Date(op.timestamp).getTime() < cutoff),
    );

    for (const op of toDelete) {
      await this.removePendingOperation(op.id);
    }

    if (toDelete.length > 0) {
      console.log(`🧹 Limpiadas ${toDelete.length} operaciones de cola`);
    }
  }
}

// ============ UTILS: Helpers para autenticación offline ============
// Función para generar hash SHA-256 de contraseña (usar en auth-offline.ts)
export async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// Exportar una instancia única
export const indexedDBService = new IndexedDBService();
